import { UserDTOInternalRolesEnum } from '@reposit/api-client';
import { ChangeEvent, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { AnyAction } from 'redux';

import { ClaimFilter } from '../../constants/claims';
import { OrganizationFilter } from '../../constants/organization';
import { getRepositFilterUpdate, LetOnlyFilter, REPOSIT_ALL_FILTER_OPTION } from '../../constants/reposit';
import { UsersFilter } from '../../constants/users';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { openEvidenceWorkspaceThunk } from '../../redux/claim/claim.thunk';
import { updateFilters as updateClaimFilters } from '../../redux/claim-list/claim-list.actions';
import { updateFilters as updateOrganizationFilters } from '../../redux/organization-list/organization-list.actions';
import { updateFilters as updateRepositFilters } from '../../redux/reposit-list/reposit-list.actions';
import { AppState } from '../../redux/root.reducer';
import { updateFilters as updateUserFilters } from '../../redux/user-list/user-list.actions';
import { getQuickLauncherDestinations, QuickLauncherDestination, QuickLauncherSearchKind } from './quick-launcher.util';

// Each search resets the list's status filter so a leftover filter cannot hide the results.
const SEARCH_FILTER_UPDATES: Record<QuickLauncherSearchKind, (query: string) => AnyAction> = {
  claims: (query) => updateClaimFilters({ filter: ClaimFilter.ALL, query }),
  organisations: (query) => updateOrganizationFilters({ filter: OrganizationFilter.ALL, query }),
  reposits: (query) =>
    updateRepositFilters({ ...getRepositFilterUpdate(REPOSIT_ALL_FILTER_OPTION), letOnly: LetOnlyFilter.ALL, query }),
  users: (query) => updateUserFilters({ filter: UsersFilter.ALL, query }),
};

export const QUICK_LAUNCHER_ERROR_MESSAGE = 'Use a Reposit, Claim, Org, or User ID, or an email address.';

export const useQuickLauncherJump = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const canOpenEvidenceWorkspace = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.EVIDENCEWORKSPACE),
  );
  const destinations = getQuickLauncherDestinations(value, { canOpenEvidenceWorkspace });
  const hasValue = value.trim().length > 0;

  const clearError = useCallback(() => {
    setError('');
  }, []);

  const reset = useCallback(() => {
    setValue('');
    clearError();
  }, [clearError]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.currentTarget.value);
      clearError();
    },
    [clearError],
  );

  const jumpTo = useCallback(
    (nextDestination: QuickLauncherDestination) => {
      reset();

      switch (nextDestination.kind) {
        case 'evidence-workspace':
          dispatch(openEvidenceWorkspaceThunk({ claimId: nextDestination.id }));
          return;
        case 'search':
          dispatch(SEARCH_FILTER_UPDATES[nextDestination.searchKind](nextDestination.id));
          history.push(nextDestination.path);
          return;
        case 'detail':
          history.push(nextDestination.path);
          return;
      }
    },
    [dispatch, history, reset],
  );

  const attemptJump = useCallback(
    (rawValue: string = value) => {
      const nextDestination = getQuickLauncherDestinations(rawValue, { canOpenEvidenceWorkspace })[0];

      if (!nextDestination) {
        if (rawValue.trim().length > 0) {
          setError(QUICK_LAUNCHER_ERROR_MESSAGE);
        }
        return false;
      }

      jumpTo(nextDestination);
      return true;
    },
    [canOpenEvidenceWorkspace, value, jumpTo],
  );

  return { attemptJump, clearError, destinations, error, handleChange, hasValue, jumpTo, reset, value };
};
